import { useState, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import Header from "@/components/Header";
import AdminSidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  FlaskConical, 
  Calendar, 
  AlertTriangle, 
  UserCheck, 
  Plus, 
  Search, 
  Download, 
  Printer,
  Camera,
  Clock,
  Users,
  CheckCircle2,
  XCircle,
  FileText,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit,
  Eye,
  Save
} from "lucide-react";
import { format, addDays, startOfWeek, isSameDay, parseISO } from "date-fns";
import { db } from "@/firebase/config";
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where, getDocs, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, onAuthStateChanged, getIdTokenResult } from "firebase/auth";

// Types
interface Lab {
  id: string;
  name: string;
  capacity: number;
  description: string;
  type: string; // Changed from fixed union to string for custom types
}

interface TimeSlot {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

interface Booking {
  id: string;
  labId: string;
  date: string;
  slotId: string;
  className: string;
  subject: string;
  teacherName: string;
  notes?: string;
  studentCount?: number; // Added for capacity check
}

interface Incident {
  id: string;
  labId: string;
  date: string;
  studentName: string;
  studentId: string;
  itemBroken: string;
  description: string;
  costEstimate: number;
  status: "pending" | "cleared";
  photos: string[];
  notes: string;
  fineAmount: number;
}

interface Student {
  id: string;
  name: string;
  class: string;
  isGraduating: boolean;
  clearanceStatus: "cleared" | "not-cleared" | "pending";
  clearanceReason?: string;
  photoUrl?: string;
}

// Sample Data
const sampleLabs: Lab[] = [
  { id: "lab-1", name: "Physics Lab", capacity: 30, description: "Equipped with mechanics and optics equipment", type: "science" },
  { id: "lab-2", name: "Chemistry Lab", capacity: 25, description: "Full chemical analysis setup with fume hoods", type: "science" },
  { id: "lab-3", name: "Biology Lab", capacity: 28, description: "Microscopes and specimen storage", type: "science" },
  { id: "lab-4", name: "Computer Lab A", capacity: 35, description: "Windows workstations with programming software", type: "computer" },
  { id: "lab-5", name: "Computer Lab B", capacity: 30, description: "Mac workstations for design and media", type: "computer" },
];

const sampleTimeSlots: TimeSlot[] = [
  { id: "slot-1", name: "Period 1", startTime: "08:00", endTime: "09:00" },
  { id: "slot-2", name: "Period 2", startTime: "09:00", endTime: "10:00" },
  { id: "slot-3", name: "Period 3", startTime: "10:30", endTime: "11:30" },
  { id: "slot-4", name: "Period 4", startTime: "11:30", endTime: "12:30" },
  { id: "slot-5", name: "Period 5", startTime: "14:00", endTime: "15:00" },
  { id: "slot-6", name: "Period 6", startTime: "15:00", endTime: "16:00" },
];

const sampleStudents: Student[] = [
  { id: "STU001", name: "John Smith", class: "12A", isGraduating: true, clearanceStatus: "pending", photoUrl: "https://i.pravatar.cc/150?u=STU001" },
  { id: "STU002", name: "Emily Johnson", class: "12B", isGraduating: true, clearanceStatus: "cleared", photoUrl: "https://i.pravatar.cc/150?u=STU002" },
  { id: "STU003", name: "Michael Brown", class: "12A", isGraduating: true, clearanceStatus: "not-cleared", clearanceReason: "Outstanding breakage fine" },
  { id: "STU004", name: "Sarah Davis", class: "11A", isGraduating: false, clearanceStatus: "pending", photoUrl: "https://i.pravatar.cc/150?u=STU004" },
  { id: "STU005", name: "James Wilson", class: "12C", isGraduating: true, clearanceStatus: "pending" },
];

const LabManagement = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [labs, setLabs] = useState<Lab[]>([]); // Initialize empty, fetch from Firebase
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [students, setStudents] = useState<Student[]>([]); // REMOVED sampleStudents
  const [timeSlots] = useState<TimeSlot[]>(sampleTimeSlots);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLab, setSelectedLab] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedClass, setSelectedClass] = useState<string>("all"); // Added Class Filter State
  
  // Booking section state (AcademicsManagement pattern)
  const [bookingMode, setBookingMode] = useState<"view"|"edit">("view");
  const [bookingLabFilter, setBookingLabFilter] = useState<string>("all");
  
  // Draft / Planning Mode State
  const [draftBookings, setDraftBookings] = useState<Booking[]>([]);
  const [isPlanning, setIsPlanning] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [planningLabId, setPlanningLabId] = useState<string>("");
  const [classes, setClasses] = useState<{id: string, name: string}[]>([]);
  const [bookingClassFilter, setBookingClassFilter] = useState<string>("all");

  // Dialog states
  const [showLabDialog, setShowLabDialog] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [showClearanceDialog, setShowClearanceDialog] = useState(false);
  const [editingLab, setEditingLab] = useState<Lab | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [lookedUpStudent, setLookedUpStudent] = useState<Student | null>(null);
  const [schoolID, setSchoolID] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  // Firestore Real-time Listener for Classes
  useEffect(() => {
    if (!schoolID) return;
    const q = query(collection(db, "schools", schoolID, "classes"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
       const classesData = snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name || doc.id }));
       // Sort classes?
       classesData.sort((a, b) => a.name.localeCompare(b.name));
       setClasses(classesData);
    }, (error) => {
       console.error("Error fetching classes:", error);
    });
    return () => unsubscribe();
  }, [schoolID]);

  // Firestore Real-time Listener for Students
  useEffect(() => {
    if (!schoolID) return;

    const q = query(collection(db, "schools", schoolID, "students"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const studentsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: data.registrationNumber || doc.id, // Use reg number for display, doc.id for updates if needed (using reg number as ID in this system mainly)
          docId: doc.id, // Keep true doc ID for updates
          name: `${data.firstName} ${data.middleName || ''} ${data.lastName}`.trim(),
          class: data.className || `${data.grade} ${data.class || ''}`,
          isGraduating: true, // Show ALL students in clearance for now to ensure visibility
          clearanceStatus: data.clearanceStatus || "pending",
          clearanceReason: data.clearanceReason || "",
          photoUrl: data.photoUrl || data.photoURL || null
        };
      }) as Student[];
      
      console.log("🎓 [LabManagement] Fetched students:", studentsData.length);
      if (studentsData.length > 0) {
        console.log("🔍 [Debug] First student grade/class:", studentsData[0].class);
      }
      setStudents(studentsData);
    }, (error) => {
      console.error("Error fetching students:", error);
      toast.error("Failed to load students");
    });

    return () => unsubscribe();
  }, [schoolID]);

  // Form states
  const [labForm, setLabForm] = useState<Omit<Lab, "id">>({ name: "", capacity: 30, description: "", type: "science" });
  const [customType, setCustomType] = useState("");
  const [isCustomType, setIsCustomType] = useState(false);
  const [bookingForm, setBookingForm] = useState<Partial<Booking>>({
    labId: "", date: "", slotId: "", teacherName: "", className: "", subject: "", notes: "", studentCount: 0
  });
  const [incidentForm, setIncidentForm] = useState<Partial<Incident>>({ labId: "", date: format(new Date(), "yyyy-MM-dd"), studentName: "", studentId: "", itemBroken: "", description: "", costEstimate: 0, status: "pending", photos: [], notes: "", fineAmount: 0 });
  const [incidentPhotos, setIncidentPhotos] = useState<string[]>([]);

  // Authentication and SchoolID Fetch
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await getIdTokenResult(user);
          const claims = idTokenResult.claims;
          // Prioritize top-level schoolID claim, then check affiliations
          if (claims.schoolID) {
              setSchoolID(claims.schoolID as string);
          } else if (claims.affiliations) {
              const affiliations = claims.affiliations as any[];
              const defaultAff = claims.defaultAffiliation as any;
              
              if (defaultAff && defaultAff.schoolID) {
                   setSchoolID(defaultAff.schoolID);
              } else if (affiliations && affiliations.length > 0) {
                   setSchoolID(affiliations[0].schoolID);
              }
          }
          // Set role for UI logic if needed
        } catch (error) {
           console.error("Error fetching claims:", error);
           toast.error("Authentication error");
        }
      } else {
         setSchoolID("");
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Firestore Real-time Listener for Labs
  useEffect(() => {
    if (!schoolID) return;

    const q = query(collection(db, "schools", schoolID, "labs"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const labsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Lab[];
      setLabs(labsData);
    }, (error) => {
      console.error("Error fetching labs:", error);
      toast.error("Failed to load labs");
    });

    return () => unsubscribe();
  }, [schoolID]);

  // Firestore Real-time Listener for Bookings and Incidents
  useEffect(() => {
    if (!schoolID) return;

    // Listen for Bookings
    const bookingsQuery = query(collection(db, "schools", schoolID, "bookings"));
    const unsubBookings = onSnapshot(bookingsQuery, (snapshot) => {
      const bookingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Booking[];
      setBookings(bookingsData);
    }, (error) => console.error("Error fetching bookings:", error));

    // Listen for Incidents
    const incidentsQuery = query(collection(db, "schools", schoolID, "incidents"));
    const unsubIncidents = onSnapshot(incidentsQuery, (snapshot) => {
      const incidentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Incident[];
      setIncidents(incidentsData);
    }, (error) => console.error("Error fetching incidents:", error));

    return () => {
        unsubBookings();
        unsubIncidents();
    };
  }, [schoolID]);

  const loadSampleData = () => {
    // Merge sample data with existing data, avoiding duplicates if already loaded
    setLabs(prev => {
        const newLabs = sampleLabs.filter(s => !prev.some(p => p.id === s.id));
        return [...prev, ...newLabs];
    });
    setStudents(prev => {
        const newStudents = sampleStudents.filter(s => !prev.some(p => p.id === s.id));
        return [...prev, ...newStudents];
    });
    
    // Generate sample bookings
    const today = new Date();
    const sampleBookings: Booking[] = [
      { id: "book-1", labId: "lab-1", date: format(today, "yyyy-MM-dd"), slotId: "slot-1", className: "10A", subject: "Physics", teacherName: "Mr. Anderson", notes: "Mechanics practical" },
      { id: "book-2", labId: "lab-2", date: format(today, "yyyy-MM-dd"), slotId: "slot-3", className: "11B", subject: "Chemistry", teacherName: "Mrs. Smith", notes: "Titration experiment" },
      { id: "book-3", labId: "lab-4", date: format(addDays(today, 1), "yyyy-MM-dd"), slotId: "slot-2", className: "12A", subject: "Computer Science", teacherName: "Mr. Johnson", notes: "Programming test" },
      { id: "book-4", labId: "lab-3", date: format(addDays(today, 2), "yyyy-MM-dd"), slotId: "slot-4", className: "9C", subject: "Biology", teacherName: "Dr. Williams", notes: "Microscopy session" },
    ];
    setBookings(prev => {
        const newBookings = sampleBookings.filter(s => !prev.some(p => p.id === s.id));
        return [...prev, ...newBookings];
    });

    // Generate sample incidents
    const sampleIncidents: Incident[] = [
      { id: "inc-1", labId: "lab-1", date: format(addDays(today, -5), "yyyy-MM-dd"), studentName: "John Smith", studentId: "STU001", itemBroken: "Oscilloscope", description: "Dropped during experiment", costEstimate: 150, status: "pending", photos: [], notes: "", fineAmount: 0 },
      { id: "inc-2", labId: "lab-2", date: format(addDays(today, -3), "yyyy-MM-dd"), studentName: "Michael Brown", studentId: "STU003", itemBroken: "Beaker Set", description: "Knocked off bench accidentally", costEstimate: 45, status: "pending", photos: [], notes: "Student agreed to pay", fineAmount: 45 },
      { id: "inc-3", labId: "lab-4", date: format(addDays(today, -1), "yyyy-MM-dd"), studentName: "Sarah Davis", studentId: "STU004", itemBroken: "Keyboard", description: "Spilled water on keyboard", costEstimate: 30, status: "cleared", photos: [], notes: "Dried and working", fineAmount: 0 },
    ];
    setIncidents(prev => {
        const newIncidents = sampleIncidents.filter(s => !prev.some(p => p.id === s.id));
        return [...prev, ...newIncidents];
    });
  };

  // Lab CRUD
  // Lab CRUD
  const handleSaveLab = async () => {
    if (!labForm.name || !labForm.capacity || !labForm.description) {
      toast.error("Please fill all fields");
      return;
    }

    if (!schoolID) {
         setLabs([...labs, { ...labForm, id: `lab-${Date.now()}`, type: isCustomType ? customType : labForm.type } as Lab]);
         toast.success("Lab added (Local Mode)");
         setShowLabDialog(false);
         return;
    }

    // Use custom type if set
    const finalType = isCustomType ? customType : labForm.type;
    
    if (!finalType) {
      toast.error("Please select or enter a lab type");
      return;
    }

    try {
      if (editingLab) {
        await updateDoc(doc(db, "schools", schoolID, "labs", editingLab.id), {
          ...labForm,
          type: finalType,
          capacity: Number(labForm.capacity) // Ensure number
        });
        toast.success("Lab updated successfully");
      } else {
        await addDoc(collection(db, "schools", schoolID, "labs"), {
          ...labForm,
          type: finalType,
          capacity: Number(labForm.capacity),
          createdAt: new Date().toISOString()
        });
        toast.success("Lab added successfully");
      }
      setShowLabDialog(false);
      setIsCustomType(false);
      setCustomType("");
      setEditingLab(null);
      setLabForm({ name: "", capacity: 30, description: "", type: "science" });
    } catch (error) {
      console.error("Error saving lab:", error);
      toast.error("Failed to save lab");
    }
  };

  const handleDeleteLab = async (id: string) => {
    if (!schoolID) {
         setLabs(labs.filter(l => l.id !== id));
         toast.success("Lab deleted (Local Mode)");
         return;
    }

    if (window.confirm("Are you sure you want to delete this lab?")) {
      try {
        await deleteDoc(doc(db, "schools", schoolID, "labs", id));
        toast.success("Lab deleted");
      } catch (error) {
        console.error("Error deleting lab:", error);
        toast.error("Failed to delete lab");
      }
    }
  };

  // Load Draft Effect
  useEffect(() => {
    const fetchDraft = async () => {
        if (!schoolID || !isPlanning || !planningLabId) return;
        
        try {
            const draftRef = doc(db, "schools", schoolID, "labBookingDrafts", planningLabId);
            const draftSnap = await getDoc(draftRef);
            if (draftSnap.exists()) {
                const data = draftSnap.data();
                if (data.bookings) {
                    setDraftBookings(data.bookings);
                } else {
                     setDraftBookings([]);
                }
            } else {
                // Initialize from live if no draft exists for this lab
                const liveDocs = bookings.filter(b => b.labId === planningLabId);
                setDraftBookings(liveDocs);
            }
            setHasUnsavedChanges(false);
        } catch (e) {
            console.error("Error loading draft", e);
            toast.error("Failed to load draft");
        }
    };
    
    if (isPlanning && planningLabId) {
        fetchDraft();
    }
  }, [isPlanning, planningLabId, schoolID, bookings.length]); // Re-run if bookings change (initial load)? No, usually distinct.

  // Draft / Planner Logic
  const toggleBookingMode = (mode: "view" | "edit") => {
    if (mode === "edit") {
        setBookingMode("edit");
        setIsPlanning(true);
        // Ensure a lab is selected for planning context
        if (bookingLabFilter === "all" && labs.length > 0) {
            setPlanningLabId(labs[0].id);
            setBookingLabFilter(labs[0].id);
        } else {
            setPlanningLabId(bookingLabFilter);
        }
    } else {
        // Exit Edit Mode (Discard check)
        if (hasUnsavedChanges) {
            if (!window.confirm("You have unsaved changes in your plan. Discard them?")) {
                return; 
            }
        }
        setDraftBookings([]);
        setHasUnsavedChanges(false);
        setIsPlanning(false);
        setBookingMode("view");
    }
  };

  const saveProgress = async () => {
      if (!schoolID || !planningLabId) return;
      try {
          await setDoc(doc(db, "schools", schoolID, "labBookingDrafts", planningLabId), {
              bookings: draftBookings,
              updatedAt: serverTimestamp(),
              labId: planningLabId
          });
          setHasUnsavedChanges(false);
          toast.success("Progress saved to draft");
      } catch (e) {
          console.error("Save progress failed", e);
          toast.error("Failed to save draft");
      }
  };

  const publishSchedule = async () => {
      if (!schoolID) {
          setBookings([...draftBookings]);
          setBookingMode("view");
          setHasUnsavedChanges(false);
          toast.success("Schedule published (Local Mode)");
          return;
      }
      
      if (!planningLabId) return;

      try {
          // 1. Get all Live bookings for this lab
          const liveQuery = query(collection(db, "schools", schoolID, "bookings"), where("labId", "==", planningLabId));
          const liveDocs = await getDocs(liveQuery);
          const liveIds = new Set(liveDocs.docs.map(d => d.id));
          
          const batchPromises = [];
          const draftMap = new Map(draftBookings.map(b => [b.id, b]));
          
          // 2. Add/Update logic
          for (const [id, booking] of draftMap) {
              if (liveIds.has(id)) {
                   batchPromises.push(updateDoc(doc(db, "schools", schoolID, "bookings", id), booking as any));
                   liveIds.delete(id); 
              } else {
                   batchPromises.push(addDoc(collection(db, "schools", schoolID, "bookings"), booking));
              }
          }
          
          // 3. Delete remaining (was in Live, not in Draft) - Overwrite logic
          for (const id of liveIds) {
               batchPromises.push(deleteDoc(doc(db, "schools", schoolID, "bookings", id)));
          }
          
          await Promise.all(batchPromises);
          
          // Save draft as clean
          await saveProgress();
          
          toast.success("Schedule published successfully!");
          setBookingMode("view");
          setIsPlanning(false);
          setHasUnsavedChanges(false);
          
      } catch (error) {
          console.error("Error publishing:", error);
          toast.error("Failed to publish schedule");
      }
  };

  // Booking functions
  const handleSaveBooking = () => {
    if (!bookingForm.labId || !bookingForm.date || !bookingForm.slotId || !bookingForm.teacherName || !bookingForm.className || !bookingForm.subject) {
      toast.error("Please fill in all required fields (Lab, Date, Slot, Teacher, Class, Subject)");
      return;
    }

    // 1. Past Date Validation
    const selectedDate = new Date(bookingForm.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for fair comparison
    if (selectedDate < today) {
      toast.error("Cannot book slots in the past!");
      return;
    }

    // 2. Capacity Check
    const selectedLabData = labs.find(l => l.id === bookingForm.labId);
    if (selectedLabData && bookingForm.studentCount && bookingForm.studentCount > selectedLabData.capacity) {
      if (!window.confirm(`Warning: Class size (${bookingForm.studentCount}) exceeds lab capacity (${selectedLabData.capacity}). Continue?`)) {
        return;
      }
    }

    // 3. Conflict Check
    // Determine which dataset to check against
    const targetBookings = bookingMode === "edit" ? draftBookings : bookings;
    
    const conflict = targetBookings.find(b => 
      b.labId === bookingForm.labId && 
      b.date === bookingForm.date && 
      b.slotId === bookingForm.slotId &&
      b.id !== selectedBooking?.id
    );

    if (conflict) {
      toast.error(`Conflict: Slot booked by ${conflict.teacherName} for ${conflict.subject} (${conflict.className})`);
      return;
    }

    if (bookingMode === "edit") {
        // DRAFT SAVE
        if (selectedBooking) {
            setDraftBookings(draftBookings.map(b => b.id === selectedBooking.id ? { ...b, ...bookingForm } as Booking : b));
        } else {
            const newBooking: Booking = {
                ...bookingForm as Booking,
                id: `draft-${Date.now()}`
            };
            setDraftBookings([...draftBookings, newBooking]);
        }
        setHasUnsavedChanges(true);
        toast.info("Added to plan (Unsaved)");
    } else {
        // LIVE SAVE (View Mode/Direct)
        // Note: View mode usually shouldn't allow saving, but we keep this for backward compatibility if needed
        // Ideally View Mode is Read Only.
        if (selectedBooking) {
          setBookings(bookings.map(b => b.id === selectedBooking.id ? { ...b, ...bookingForm } as Booking : b));
          toast.success("Booking updated!");
        } else {
          // This branch might not be reachable if we hide "New Booking" in view mode
          const newBooking: Booking = {
            ...bookingForm as Booking,
            id: `book-${Date.now()}`
          };
          setBookings([...bookings, newBooking]);
          toast.success("Booking created!");
        }
    }
    
    setShowBookingDialog(false);
    setBookingForm({ labId: "", date: "", slotId: "", teacherName: "", className: "", subject: "", notes: "", studentCount: 0 });
    setSelectedBooking(null);
  };



  // Incident functions
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setIncidentPhotos(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleSaveIncident = async () => {
    if (!incidentForm.labId || !incidentForm.itemBroken) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Auto-populate student name from ID if not present
    let finalStudentName = incidentForm.studentName;
    if (incidentForm.studentId && !finalStudentName) {
        if (lookedUpStudent && lookedUpStudent.id === incidentForm.studentId) {
             finalStudentName = lookedUpStudent.name;
        } else {
             // Fallback to local search if lookedUpStudent state is missing
             const student = students.find(s => s.id === incidentForm.studentId);
             finalStudentName = student ? student.name : "Unknown Student";
        }
    }

    const finalIncidentForm = { ...incidentForm, studentName: finalStudentName };

    if (!schoolID) {
      if (selectedIncident) {
        setIncidents(incidents.map(i => i.id === selectedIncident.id ? { ...i, ...finalIncidentForm, photos: incidentPhotos } as Incident : i));
        toast.success("Incident updated! (Local Mode)");
      } else {
        const newIncident: Incident = {
          ...finalIncidentForm as Incident,
          id: `inc-${Date.now()}`,
          photos: incidentPhotos
        };
        setIncidents([...incidents, newIncident]);
        toast.success("Incident reported! (Local Mode)");
      }
      setShowIncidentDialog(false);
      setSelectedIncident(null);
      setIncidentForm({ labId: "", date: format(new Date(), "yyyy-MM-dd"), studentName: "", studentId: "", itemBroken: "", description: "", costEstimate: 0, status: "pending", photos: [], notes: "", fineAmount: 0 });
      setIncidentPhotos([]);
      return;
    }

    try {
      if (selectedIncident) {
        await updateDoc(doc(db, "schools", schoolID, "incidents", selectedIncident.id), {
          ...finalIncidentForm,
          photos: incidentPhotos
        });
        toast.success("Incident updated successfully");
      } else {
        await addDoc(collection(db, "schools", schoolID, "incidents"), {
          ...finalIncidentForm,
          photos: incidentPhotos,
          createdAt: new Date().toISOString()
        });
        toast.success("Incident reported successfully");
      }
      
      setShowIncidentDialog(false);
      setSelectedIncident(null);
      setIncidentForm({ labId: "", date: format(new Date(), "yyyy-MM-dd"), studentName: "", studentId: "", itemBroken: "", description: "", costEstimate: 0, status: "pending", photos: [], notes: "", fineAmount: 0 });
      setIncidentPhotos([]);
    } catch (error) {
       console.error("Error saving incident:", error);
       toast.error("Failed to save incident");
    }
  };

  // Clearance functions
  const handleClearStudent = async (student: Student, cleared: boolean, reason?: string) => {
    if (!schoolID) return;

    try {
      // Find the document ID. We need the Firestore document ID, not just the registration number.
      // In the listener above, I added 'docId'. Let's verify the interface update.
      const studentDocId = (student as any).docId; 
      
      if (!studentDocId) {
        toast.error("Error: Could not identify student document.");
        return;
      }

      const studentRef = doc(db, "schools", schoolID, "students", studentDocId);
      const updateData = {
        clearanceStatus: cleared ? "cleared" : "not-cleared",
        clearanceReason: reason || (cleared ? null : "No reason provided")
      };

      await updateDoc(studentRef, updateData);
      
      toast.success(cleared ? "Student cleared successfully" : "Student marked as not cleared");
      setShowClearanceDialog(false);
    } catch (error) {
      console.error("Error updating clearance:", error);
      toast.error("Failed to update clearance status");
    }
  };

  // Helper functions
  const getLabName = (labId: string) => labs.find(l => l.id === labId)?.name || "Unknown Lab";
  const getSlotInfo = (slotId: string) => timeSlots.find(s => s.id === slotId);

  const getBookingsForSlot = (date: Date, slotId: string) => {
    const source = bookingMode === "edit" ? draftBookings : bookings;
    return source.filter(b => b.date === format(date, "yyyy-MM-dd") && b.slotId === slotId);
  };

  const getStudentIncidents = (studentId: string) => {
    return incidents.filter(i => i.studentId === studentId);
  };

  const filteredIncidents = incidents.filter(i => {
    const matchesSearch = i.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         i.itemBroken.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLab = selectedLab === "all" || i.labId === selectedLab;
    const matchesStatus = selectedStatus === "all" || i.status === selectedStatus;
    return matchesSearch && matchesLab && matchesStatus;
  });

  /* 
    DERIVED STATE: Effective Clearance Status
    - If student has pending incidents -> Status is AUTOMATICALLY 'not-cleared'.
    - Otherwise -> Status is what is stored in DB.
  */
  const graduatingStudents = students.filter(s => s.isGraduating).map(student => {
    const studentIncidents = getStudentIncidents(student.id);
    const hasPendingIncidents = studentIncidents.some(i => i.status === "pending");
    
    // Calculate effective status
    let effectiveStatus = student.clearanceStatus;
    let effectiveReason = student.clearanceReason;

    if (hasPendingIncidents) {
      effectiveStatus = "not-cleared";
      effectiveReason = "Automatic Block: Outstanding Incidents";
    }

    return {
      ...student,
      effectiveStatus,
      effectiveReason,
      hasPendingIncidents
    };
  }).filter(s => selectedClass === "all" || s.class === selectedClass); // Apply Class Filter

  // Extract unique classes for the filter dropdown
  const uniqueClasses = Array.from(new Set(students.map(s => s.class))).sort();

  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(currentWeekStart, i));

  // Export functions
  const exportToPDF = () => {
    toast.success("Exporting to PDF...");
    window.print();
  };

  const exportToCSV = () => {
    const headers = ["Date", "Time", "Lab", "Class", "Subject", "Teacher"];
    const rows = bookings.map(b => {
      const slot = getSlotInfo(b.slotId);
      return [b.date, slot ? `${slot.startTime}-${slot.endTime}` : "", getLabName(b.labId), b.className, b.subject, b.teacherName];
    });
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lab_bookings.csv";
    a.click();
    toast.success("CSV exported!");
  };

  // Dashboard stats
  const todayBookings = bookings.filter(b => b.date === format(new Date(), "yyyy-MM-dd"));
  const pendingIncidents = incidents.filter(i => i.status === "pending");
  const pendingClearances = students.filter(s => s.isGraduating && s.clearanceStatus === "pending");

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto dark:bg-[#020817] stable-gutter">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
            <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
              <FlaskConical className="h-7 w-7 text-primary" />
              Laboratory Management
            </h1>
            <p className="text-muted-foreground mt-1">Manage labs, bookings, incidents, and student clearances</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadSampleData}>
              <Plus className="h-4 w-4 mr-1" /> Load Sample Data
            </Button>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 h-auto">
            <TabsTrigger value="dashboard" className="text-xs md:text-sm py-2">Dashboard</TabsTrigger>
            <TabsTrigger value="labs" className="text-xs md:text-sm py-2">Labs</TabsTrigger>
            <TabsTrigger value="bookings" className="text-xs md:text-sm py-2">Bookings</TabsTrigger>
            <TabsTrigger value="incidents" className="text-xs md:text-sm py-2">Incidents</TabsTrigger>
            <TabsTrigger value="clearance" className="text-xs md:text-sm py-2">Clearance</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-200/50 dark:border-blue-800/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <FlaskConical className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Labs</p>
                      <p className="text-2xl font-bold text-foreground">{labs.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-200/50 dark:border-green-800/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Today's Bookings</p>
                      <p className="text-2xl font-bold text-foreground">{todayBookings.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-200/50 dark:border-amber-800/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Incidents</p>
                      <p className="text-2xl font-bold text-foreground">{pendingIncidents.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-200/50 dark:border-purple-800/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <UserCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Clearances</p>
                      <p className="text-2xl font-bold text-foreground">{pendingClearances.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Today's Schedule */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Today's Lab Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayBookings.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No bookings for today</p>
                ) : (
                  <div className="space-y-3">
                    {todayBookings.map(booking => {
                      const slot = getSlotInfo(booking.slotId);
                      return (
                        <div key={booking.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/40 m-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded">
                              <FlaskConical className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{getLabName(booking.labId)}</p>
                              <p className="text-sm text-muted-foreground">{booking.className} - {booking.subject}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-foreground">{slot?.name}</p>
                            <p className="text-sm text-muted-foreground">{slot?.startTime} - {slot?.endTime}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Incidents */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Recent Incidents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {incidents.slice(0, 5).length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No incidents reported</p>
                ) : (
                  <div className="space-y-3">
                    {incidents.slice(0, 5).map(incident => (
                      <div key={incident.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/40 m-2">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded ${
                            incident.status === "pending" ? "bg-amber-500/20" : "bg-green-500/20"
                          }`}>
                            <AlertTriangle className={`h-4 w-4 ${
                              incident.status === "pending" ? "text-amber-600" : "text-green-600"
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{incident.itemBroken}</p>
                            <p className="text-sm text-muted-foreground">{incident.studentName} - {getLabName(incident.labId)}</p>
                          </div>
                        </div>
                        <Badge variant={
                          incident.status === "pending" ? "destructive" : "default"
                        }>
                          {incident.status === "pending" ? "Pending" : "Cleared"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Labs Tab */}
          <TabsContent value="labs" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-foreground">Labs & Resources</h2>
              <Dialog open={showLabDialog} onOpenChange={setShowLabDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all px-6"
                    onClick={() => { setEditingLab(null); setLabForm({ name: "", capacity: 30, description: "", type: "science" }); }}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Lab
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md border-border/50 shadow-xl bg-gradient-to-b from-background to-muted/20">
                  <DialogHeader>
                    <DialogTitle>{editingLab ? "Edit Lab" : "Add New Lab"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Lab Name</Label>
                      <Input 
                        value={labForm.name} 
                        onChange={e => setLabForm({ ...labForm, name: e.target.value })}
                        placeholder="e.g., Physics Lab"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select 
                        value={["science", "computer", "physics", "chemistry"].includes(labForm.type) ? labForm.type : (labForm.type ? "custom" : undefined)} 
                        onValueChange={v => {
                          if (v === "custom") {
                            setIsCustomType(true);
                            setCustomType("");
                            setLabForm({ ...labForm, type: "" });
                          } else {
                            setIsCustomType(false);
                            setLabForm({ ...labForm, type: v });
                          }
                        }}
                      >
                        <SelectTrigger className="border-border/50 hover:border-primary/50 transition-colors">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="border-border shadow-md bg-popover">
                          <SelectItem value="science" className="cursor-pointer focus:bg-emerald-50 focus:text-emerald-700 dark:focus:bg-emerald-500/10 dark:focus:text-emerald-400">Science</SelectItem>
                          <SelectItem value="computer" className="cursor-pointer focus:bg-blue-50 focus:text-blue-700 dark:focus:bg-blue-500/10 dark:focus:text-blue-400">Computer</SelectItem>
                          <SelectItem value="physics" className="cursor-pointer focus:bg-violet-50 focus:text-violet-700 dark:focus:bg-violet-500/10 dark:focus:text-violet-400">Physics</SelectItem>
                          <SelectItem value="chemistry" className="cursor-pointer focus:bg-amber-50 focus:text-amber-700 dark:focus:bg-amber-500/10 dark:focus:text-amber-400">Chemistry</SelectItem>
                          <SelectItem value="custom" className="cursor-pointer focus:bg-indigo-50 focus:text-indigo-700 dark:focus:bg-indigo-500/10 dark:focus:text-indigo-400 font-medium">Custom Type...</SelectItem>
                        </SelectContent>
                      </Select>
                      {isCustomType && (
                        <div className="mt-2 animate-in fade-in slide-in-from-top-1 duration-200">
                          <Input 
                            value={customType}
                            onChange={(e) => {
                                setCustomType(e.target.value);
                                setLabForm({ ...labForm, type: e.target.value });
                            }}
                            placeholder="Enter custom lab type (e.g. Robotics, Art)"
                            className="bg-muted/10 border-primary/20 focus:border-primary/50" 
                          />
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Capacity</Label>
                      <Input 
                        type="number"
                        value={labForm.capacity} 
                        onChange={e => setLabForm({ ...labForm, capacity: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea 
                        value={labForm.description} 
                        onChange={e => setLabForm({ ...labForm, description: e.target.value })}
                        placeholder="Equipment and features available"
                      />
                    </div>
                  </div>
                  <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => setShowLabDialog(false)} className="hover:bg-muted/50">Cancel</Button>
                    <Button 
                      onClick={handleSaveLab} 
                      className="ml-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md text-white border-0"
                    >
                      {editingLab ? "Update" : "Add"} Lab
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {labs.map(lab => (
                <Card key={lab.id} className="hover:shadow-md transition-shadow border-border/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-2.5 rounded-xl shadow-sm ${
                          lab.type === "science" ? "bg-gradient-to-br from-green-500/20 to-emerald-500/10 border border-green-500/10" :
                          lab.type === "computer" ? "bg-gradient-to-br from-blue-500/20 to-indigo-500/10 border border-blue-500/10" :
                          lab.type === "physics" ? "bg-gradient-to-br from-violet-500/20 to-purple-500/10 border border-violet-500/10" :
                          lab.type === "chemistry" ? "bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border border-amber-500/10" :
                          "bg-gradient-to-br from-gray-500/20 to-slate-500/10 border border-gray-500/10"
                        }`}>
                          <FlaskConical className={`h-5 w-5 ${
                            lab.type === "science" ? "text-green-600" :
                            lab.type === "computer" ? "text-blue-600" :
                            lab.type === "physics" ? "text-violet-600" :
                            lab.type === "chemistry" ? "text-amber-600" :
                            "text-gray-600"
                          }`} />
                        </div>
                        <div>
                          <CardTitle className="text-base">{lab.name}</CardTitle>
                          <Badge variant="outline" className={`mt-1 text-xs font-normal border ${
                            lab.type === "science" ? "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20" :
                            lab.type === "computer" ? "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20" :
                            lab.type === "physics" ? "bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20" :
                            lab.type === "chemistry" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20" :
                            "bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20"
                          }`}>
                            {lab.type}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => { setEditingLab(lab); setLabForm(lab); setShowLabDialog(true); }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteLab(lab.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-2">{lab.description}</p>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-foreground">Capacity: {lab.capacity}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            
            {/* Header Controls: Mode Toggle & Save Actions */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                 {/* Mode Toggle Group */}
                 <div className="bg-muted/30 p-1 rounded-xl border border-border/50 inline-flex w-fit">
                    <button
                        onClick={() => toggleBookingMode("view")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            bookingMode === "view" 
                            ? "bg-white dark:bg-slate-800 shadow-sm text-primary ring-1 ring-border/5" 
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        <Eye className="h-4 w-4" />
                        View Live Schedule
                    </button>
                    <button
                        onClick={() => toggleBookingMode("edit")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            bookingMode === "edit" 
                            ? "bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 shadow-sm ring-1 ring-amber-200/50" 
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        <Edit className="h-4 w-4" />
                        Plan & Edit
                    </button>
                 </div>

                 {/* Planner Actions */}
                 {bookingMode === "edit" && (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4">
                         {hasUnsavedChanges && (
                            <div className="flex items-center gap-2 mr-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium rounded-full">
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                                </span>
                                Unsaved Changes
                            </div>
                         )}
                         <Button 
                            variant="outline" 
                            onClick={() => toggleBookingMode("view")}
                            className="text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 dark:hover:text-red-400 border-border/60 transition-colors"
                         >
                            Discard
                         </Button>
                         <Button 
                            variant="outline"
                            onClick={saveProgress}
                            disabled={!hasUnsavedChanges}
                            className={`transition-all border shadow-sm font-medium ${
                                !hasUnsavedChanges 
                                ? "opacity-70 cursor-not-allowed bg-amber-100 text-amber-600 border-amber-200 dark:bg-amber-900/40 dark:text-amber-400 dark:border-amber-800" 
                                : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:border-amber-300 dark:hover:border-amber-700 hover:text-amber-800 dark:hover:text-amber-300"
                            }`}
                         >
                            <Save className="h-4 w-4 mr-2" />
                            Save Progress
                         </Button>
                         <Button 
                            onClick={publishSchedule}
                            disabled={!hasUnsavedChanges}
                            className={`shadow-md transition-all duration-300 font-semibold text-white ${
                                !hasUnsavedChanges
                                ? "opacity-70 cursor-not-allowed bg-gradient-to-r from-emerald-600 to-green-600 dark:from-emerald-600/70 dark:to-green-600/70"
                                : "bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 hover:shadow-lg hover:scale-[1.02] border-0 ring-1 ring-white/10 dark:ring-emerald-500/20 dark:shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                            }`}
                         >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Publish Schedule
                         </Button>
                    </div>
                 )}
              </div>

              {/* Planning Context Banner */}
              {bookingMode === "edit" && (
                  <div className="bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/40 p-4 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-amber-600 dark:text-amber-400 mt-0.5">
                             <Calendar className="h-5 w-5" />
                        </div>
                        <div>
                             <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                                 Planning Mode: {labs.find(l => l.id === planningLabId)?.name || "Select Lab"}
                             </h3>
                             <p className="text-sm text-amber-700 dark:text-amber-400/80 mt-0.5 max-w-xl">
                                 You are editing a local draft. Allocations are temporary until you click "Submit Plan". 
                                 Select a specific lab to manage its schedule.
                             </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 bg-white dark:bg-slate-900/50 p-2 rounded-lg border border-amber-100 dark:border-amber-900/30">
                         <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider pl-1">Focus Lab</span>
                         <Select value={planningLabId} onValueChange={(v) => { setPlanningLabId(v); setBookingLabFilter(v); }}>
                             <SelectTrigger className="w-[200px] h-9 border-amber-200 dark:border-amber-800 focus:ring-amber-500">
                                <SelectValue />
                             </SelectTrigger>
                             <SelectContent>
                                {labs.map(l => (
                                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                ))}
                             </SelectContent>
                         </Select>
                      </div>
                  </div>
              )}
            </div>

            {/* Stat Cards - Only Show in View Mode */}
            {bookingMode === "view" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-200/50 dark:border-blue-800/20">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-500/20 rounded-lg">
                      <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">This Week</p>
                      <p className="text-lg font-bold text-foreground">{bookings.filter(b => {
                        const bDate = new Date(b.date);
                        return bDate >= currentWeekStart && bDate <= addDays(currentWeekStart, 4);
                      }).length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-200/50 dark:border-green-800/20">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-500/20 rounded-lg">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Available Slots</p>
                      <p className="text-lg font-bold text-foreground">{(labs.length * timeSlots.length * 5) - bookings.filter(b => {
                        const bDate = new Date(b.date);
                        return bDate >= currentWeekStart && bDate <= addDays(currentWeekStart, 4);
                      }).length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-200/50 dark:border-purple-800/20">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-500/20 rounded-lg">
                      <FlaskConical className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Labs</p>
                      <p className="text-lg font-bold text-foreground">{labs.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-200/50 dark:border-amber-800/20">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-500/20 rounded-lg">
                      <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Time Slots</p>
                      <p className="text-lg font-bold text-foreground">{timeSlots.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            )}

            {/* Controls Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-medium text-foreground">
                  {format(currentWeekStart, "MMM d")} - {format(addDays(currentWeekStart, 4), "MMM d, yyyy")}
                </span>
                <Button variant="outline" size="icon" onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                
                {/* Lab Filter - Only show in View mode (Edit mode has forced banner selector) */}
                {bookingMode === "view" && (
                  <Select value={bookingLabFilter} onValueChange={setBookingLabFilter}>
                    <SelectTrigger className="w-[160px] ml-2">
                      <SelectValue placeholder="Filter by lab" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Labs</SelectItem>
                      {labs.map(lab => (
                        <SelectItem key={lab.id} value={lab.id}>{lab.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Class Filter - Available in both modes */}
                <Select value={bookingClassFilter} onValueChange={setBookingClassFilter}>
                   <SelectTrigger className="w-[160px] ml-2">
                      <SelectValue placeholder="Filter by Class" />
                   </SelectTrigger>
                   <SelectContent>
                      <SelectItem value="all">All Classes</SelectItem>
                      {classes.map(cls => (
                        <SelectItem key={cls.id} value={cls.name}>{cls.name}</SelectItem>
                      ))}
                   </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportToCSV}>
                  <Download className="h-4 w-4 mr-1" /> Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={exportToPDF}>
                  <Printer className="h-4 w-4 mr-1" /> Print
                </Button>
                <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" onClick={() => { 
                      setSelectedBooking(null); 
                      setBookingForm({ 
                        labId: bookingLabFilter !== 'all' ? bookingLabFilter : "", 
                        date: "", 
                        slotId: "", 
                        className: bookingClassFilter !== 'all' ? bookingClassFilter : "", 
                        subject: "", 
                        teacherName: "", 
                        notes: "" 
                      }); 
                    }}>
                      <Plus className="h-4 w-4 mr-1" /> New Booking
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md border-border/50 shadow-xl bg-gradient-to-b from-background to-muted/20">
                    <DialogHeader>
                      <DialogTitle>{selectedBooking ? "Edit Booking" : "New Booking"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Lab</Label>
                        <Select value={bookingForm.labId} onValueChange={v => setBookingForm({ ...bookingForm, labId: v })}>
                          <SelectTrigger className="border-border/50 hover:border-primary/50 transition-colors">
                            <SelectValue placeholder="Select lab" />
                          </SelectTrigger>
                          <SelectContent className="border-border shadow-md bg-popover">
                            {labs.map(lab => (
                              <SelectItem 
                                key={lab.id} 
                                value={lab.id}
                                className={`cursor-pointer transition-colors ${
                                  lab.type === "science" ? "hover:bg-green-500/10 hover:text-green-600 focus:bg-green-500/10 focus:text-green-600" :
                                  lab.type === "computer" ? "hover:bg-blue-500/10 hover:text-blue-600 focus:bg-blue-500/10 focus:text-blue-600" :
                                  lab.type === "physics" ? "hover:bg-violet-500/10 hover:text-violet-600 focus:bg-violet-500/10 focus:text-violet-600" :
                                  lab.type === "chemistry" ? "hover:bg-amber-500/10 hover:text-amber-600 focus:bg-amber-500/10 focus:text-amber-600" :
                                  "hover:bg-primary/10 hover:text-foreground focus:bg-primary/10 focus:text-foreground"
                                }`}
                              >
                                {lab.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input 
                            type="date"
                            value={bookingForm.date} 
                            onChange={e => setBookingForm({ ...bookingForm, date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Time Slot</Label>
                          <Select value={bookingForm.slotId} onValueChange={v => setBookingForm({ ...bookingForm, slotId: v })}>
                            <SelectTrigger className="border-border/50 hover:border-primary/50 transition-colors">
                              <SelectValue placeholder="Select slot" />
                            </SelectTrigger>
                            <SelectContent className="border-border shadow-md bg-popover">
                              {timeSlots.map(slot => (
                                <SelectItem key={slot.id} value={slot.id} className="cursor-pointer hover:bg-primary/5 focus:bg-primary/5">
                                  {slot.name} <span className="text-muted-foreground text-xs ml-1">({slot.startTime}-{slot.endTime})</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {bookingForm.labId && (
                           <div className="col-span-2 text-xs text-muted-foreground bg-muted/30 p-2 rounded border border-border/40">
                              Max Capacity: <span className="font-semibold text-foreground">{labs.find(l => l.id === bookingForm.labId)?.capacity || 0} students</span>
                           </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Class</Label>
                          <Select value={bookingForm.className} onValueChange={v => setBookingForm({ ...bookingForm, className: v })}>
                            <SelectTrigger className="border-border/50 hover:border-primary/50 transition-colors">
                              <SelectValue placeholder="Select class" />
                            </SelectTrigger>
                            <SelectContent className="border-border shadow-md bg-popover">
                              {classes.map(cls => (
                                <SelectItem key={cls.id} value={cls.name}>{cls.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Subject</Label>
                          <Input 
                            value={bookingForm.subject} 
                            onChange={e => setBookingForm({ ...bookingForm, subject: e.target.value })}
                            placeholder="e.g., Physics"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label>Teacher Name</Label>
                          <Input 
                            value={bookingForm.teacherName} 
                            onChange={e => setBookingForm({ ...bookingForm, teacherName: e.target.value })}
                            placeholder="e.g., Mr. Smith"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea 
                          value={bookingForm.notes} 
                          onChange={e => setBookingForm({ ...bookingForm, notes: e.target.value })}
                          placeholder="Any special requirements..."
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowBookingDialog(false)} className="hover:bg-muted/50">Cancel</Button>
                      <Button 
                        onClick={handleSaveBooking}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md text-white border-0 ml-2"
                      >
                        {selectedBooking ? "Update" : "Create"} Booking
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Weekly Calendar Grid */}
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24 min-w-[96px]">Time</TableHead>
                      {weekDays.map(day => (
                        <TableHead 
                          key={day.toString()} 
                          className={`min-w-[140px] text-center ${isSameDay(day, new Date()) ? "bg-primary/10" : ""}`}
                        >
                          <div className="font-semibold">{format(day, "EEE")}</div>
                          <div className="text-xs text-muted-foreground">{format(day, "MMM d")}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {timeSlots.map(slot => (
                      <TableRow key={slot.id}>
                        <TableCell className="font-medium text-sm">
                          <div>{slot.name}</div>
                          <div className="text-xs text-muted-foreground">{slot.startTime}-{slot.endTime}</div>
                        </TableCell>
                        {weekDays.map(day => {
                          const dayBookings = getBookingsForSlot(day, slot.id).filter(b => 
                            (bookingLabFilter === "all" || b.labId === bookingLabFilter) &&
                            (bookingClassFilter === "all" || b.className === bookingClassFilter)
                          );
                          const isPast = day < new Date(new Date().setHours(0,0,0,0));

                          return (
                            <TableCell 
                              key={`${day}-${slot.id}`} 
                              className={`p-1 align-top min-h-[100px] border-r border-border/20 last:border-0 ${isSameDay(day, new Date()) ? "bg-primary/5" : ""}`}
                            >
                              <div className="flex flex-col gap-1 h-full min-h-[60px]">
                                {dayBookings.map(booking => {
                                  const lab = labs.find(l => l.id === booking.labId);
                                  // Determine color based on lab type
                                  let typeStyles = "bg-primary/10 text-foreground border-border/50";
                                  if (lab?.type === 'science' || lab?.type === 'chemistry' || lab?.type === 'biology' || lab?.type === 'physics') {
                                    typeStyles = "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800";
                                  } else if (lab?.type === 'computer') {
                                    typeStyles = "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800";
                                  }

                                  return (
                                    <div 
                                      key={booking.id}
                                      className={`p-2 rounded-md text-xs border ${typeStyles} shadow-sm transition-all hover:scale-[1.02] cursor-pointer group`}
                                      onClick={() => { 
                                         // View details in both modes, but edit triggers edit dialog pre-filled
                                         if (bookingMode === 'edit') {
                                           setSelectedBooking(booking); setBookingForm(booking); setShowBookingDialog(true);
                                         }
                                         // In view mode we could show a read-only dialog, currently edit dialog works for both but saving is what matters
                                      }}
                                    >
                                      <div className="flex justify-between items-start mb-0.5">
                                        <div className="font-semibold truncate max-w-[80px]">{lab?.name}</div>
                                        {bookingMode === 'edit' && <Edit className="h-3 w-3 opacity-0 group-hover:opacity-100" />}
                                      </div>
                                      <div className="font-medium truncate">{booking.subject}</div>
                                      <div className="text-[10px] opacity-80 truncate">{booking.className} • {booking.teacherName}</div>
                                    </div>
                                  );
                                })}

                                {/* Add Button in Edit Mode */}
                                {bookingMode === 'edit' && (
                                  <div 
                                    className={`flex-1 min-h-[30px] flex items-center justify-center rounded-md border border-dashed border-border/30 hover:border-primary/50 hover:bg-primary/5 cursor-pointer text-muted-foreground/40 hover:text-primary transition-colors ${dayBookings.length === 0 ? 'h-full' : ''}`}
                                    onClick={() => {
                                      setBookingForm({ 
                                        labId: bookingLabFilter !== 'all' ? bookingLabFilter : "", 
                                        date: format(day, "yyyy-MM-dd"), 
                                        slotId: slot.id, 
                                        className: bookingClassFilter !== 'all' ? bookingClassFilter : "", 
                                        subject: "", teacherName: "", notes: "", studentCount: 0
                                      });
                                      setSelectedBooking(null);
                                      setShowBookingDialog(true);
                                    }}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search incidents..." 
                    className="pl-9 w-48"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={selectedLab} onValueChange={setSelectedLab}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Filter by lab" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Labs</SelectItem>
                    {labs.map(lab => (
                      <SelectItem key={lab.id} value={lab.id}>{lab.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="repaired">Repaired</SelectItem>
                    <SelectItem value="fined">Fined</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all px-6"
                    onClick={() => { 
                    setSelectedIncident(null); 
                    setIncidentForm({ labId: "", date: format(new Date(), "yyyy-MM-dd"), studentName: "", studentId: "", itemBroken: "", description: "", costEstimate: 0, status: "pending", photos: [], notes: "", fineAmount: 0 });
                    setIncidentPhotos([]);
                  }}>
                    <Plus className="h-4 w-4 mr-2" /> Report Incident
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg border-border/50 shadow-xl bg-gradient-to-b from-background to-muted/20">
                  <DialogHeader>
                    <DialogTitle>{selectedIncident ? "Edit Incident" : "Report Incident"}</DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[60vh]">
                    <div className="space-y-4 py-4 pr-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Lab</Label>
                          <Select value={incidentForm.labId} onValueChange={v => setIncidentForm({ ...incidentForm, labId: v })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select lab" />
                            </SelectTrigger>
                            <SelectContent className="border-border shadow-md bg-popover">
                              {labs.map(lab => (
                                <SelectItem 
                                  key={lab.id} 
                                  value={lab.id} 
                                  className={`cursor-pointer transition-colors ${
                                    lab.type === "science" ? "hover:bg-green-500/10 hover:text-green-600 focus:bg-green-500/10 focus:text-green-600" :
                                    lab.type === "computer" ? "hover:bg-blue-500/10 hover:text-blue-600 focus:bg-blue-500/10 focus:text-blue-600" :
                                    lab.type === "physics" ? "hover:bg-violet-500/10 hover:text-violet-600 focus:bg-violet-500/10 focus:text-violet-600" :
                                    lab.type === "chemistry" ? "hover:bg-amber-500/10 hover:text-amber-600 focus:bg-amber-500/10 focus:text-amber-600" :
                                    "hover:bg-primary/10 hover:text-foreground focus:bg-primary/10 focus:text-foreground"
                                  }`}
                                >
                                  {lab.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input 
                            type="date"
                            value={incidentForm.date} 
                            onChange={e => setIncidentForm({ ...incidentForm, date: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Student ID</Label>
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2 items-center">
                              <Input 
                                value={incidentForm.studentId} 
                                onChange={async (e) => {
                                  const newId = e.target.value;
                                  setIncidentForm({ ...incidentForm, studentId: newId });
                                  setLookedUpStudent(null);
                                  
                                  if (newId.length > 3 && schoolID) {
                                      // Real-time Firestore Search
                                      const q = query(
                                          collection(db, "schools", schoolID, "students"), 
                                          where("registrationNumber", "==", newId)
                                      );
                                      try {
                                          const querySnapshot = await getDocs(q);
                                          if (!querySnapshot.empty) {
                                              const docData = querySnapshot.docs[0].data();
                                              console.log("📂 [LabManagement] Student found by Registration Number:", newId);
                                              console.log("📄 [LabManagement] Document Data:", docData);
                                              const fullName = `${docData.firstName} ${docData.middleName ? docData.middleName + ' ' : ''}${docData.lastName}`;
                                              const pUrl = docData.photoUrl || docData.photoURL || null;
                                              console.log("📷 [LabManagement] Photo URL from DB:", pUrl);
                                              
                                              const student: Student = {
                                                  id: docData.registrationNumber,
                                                  name: fullName,
                                                  class: docData.className || `${docData.grade} ${docData.class || ''}`,
                                                  isGraduating: false,
                                                  clearanceStatus: "pending",
                                                  photoUrl: pUrl || undefined
                                              };
                                              setLookedUpStudent(student);
                                          }
                                      } catch (err) {
                                          console.error("Error searching student:", err);
                                      }
                                  } else {
                                     // Fallback to sample/local search if exact match (for demo/local mode)
                                     const localStudent = students.find(s => s.id.toLowerCase() === newId.toLowerCase());
                                     if (localStudent) setLookedUpStudent(localStudent);
                                  }
                                }}
                                placeholder="Enter Registration Number (e.g., STU-123)"
                                className={incidentForm.studentId && !lookedUpStudent ? "border-red-500 focus-visible:ring-red-500" : ""}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select value={incidentForm.status} onValueChange={v => setIncidentForm({ ...incidentForm, status: v as Incident["status"] })}>
                            <SelectTrigger className="border-border/50 hover:border-primary/50 transition-colors">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border-border shadow-md bg-popover">
                              <SelectItem value="pending" className="cursor-pointer hover:bg-amber-500/10 hover:text-amber-600 focus:bg-amber-500/10 focus:text-amber-600">Pending</SelectItem>
                              <SelectItem value="cleared" className="cursor-pointer hover:bg-green-500/10 hover:text-green-600 focus:bg-green-500/10 focus:text-green-600">Cleared</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Student Verification Card - Full Width */}
                        <div className="col-span-2">
                            {lookedUpStudent ? (
                              <div className="p-4 bg-card border border-border rounded-lg shadow-sm flex items-center gap-4 animate-in fade-in slide-in-from-top-2 w-full">
                                <div className="h-16 w-16 rounded-full overflow-hidden border border-border bg-muted shrink-0 text-muted-foreground flex items-center justify-center">
                                  {lookedUpStudent.photoUrl ? (
                                    <img 
                                      src={lookedUpStudent.photoUrl} 
                                      alt={lookedUpStudent.name} 
                                      className="h-full w-full object-cover"
                                      onError={() => {
                                        setLookedUpStudent(prev => prev ? { ...prev, photoUrl: undefined } : null);
                                      }}
                                    />
                                  ) : (
                                    <span className="text-2xl font-bold uppercase">{lookedUpStudent.name.charAt(0)}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 space-y-1">
                                  <h4 className="text-lg font-semibold text-foreground truncate leading-none">{lookedUpStudent.name}</h4>
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs text-foreground font-medium">{lookedUpStudent.id}</span>
                                    <span>•</span>
                                    <span className="truncate text-foreground/80">{lookedUpStudent.class}</span>
                                  </div>
                                </div>
                                <div className="text-green-500 shrink-0">
                                  <CheckCircle2 className="h-6 w-6" />
                                </div>
                              </div>
                            ) : incidentForm.studentId && incidentForm.studentId.length > 3 ? (
                              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 animate-in fade-in w-full">
                                <AlertTriangle className="h-4 w-4 text-destructive" />
                                <span className="text-sm text-destructive font-medium">Student not found in database</span>
                              </div>
                            ) : null}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Item Broken</Label>
                        <Input 
                          value={incidentForm.itemBroken} 
                          onChange={e => setIncidentForm({ ...incidentForm, itemBroken: e.target.value })}
                          placeholder="e.g., Microscope"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea 
                          value={incidentForm.description} 
                          onChange={e => setIncidentForm({ ...incidentForm, description: e.target.value })}
                          placeholder="What happened?"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Cost Estimate (Ksh)</Label>
                          <Input 
                            type="number"
                            value={incidentForm.costEstimate} 
                            onChange={e => setIncidentForm({ ...incidentForm, costEstimate: parseFloat(e.target.value) })}
                          />
                        </div>

                      </div>

                      <div className="space-y-2">
                          <Label>Fine Amount (Ksh)</Label>
                          <Input 
                            type="number"
                            value={incidentForm.fineAmount} 
                            onChange={e => setIncidentForm({ ...incidentForm, fineAmount: parseFloat(e.target.value) })}
                          />
                      </div>
                      <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea 
                          value={incidentForm.notes} 
                          onChange={e => setIncidentForm({ ...incidentForm, notes: e.target.value })}
                          placeholder="Additional notes..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Photos</Label>
                        <div className="border border-dashed border-border rounded-lg p-4">
                          <input 
                            type="file" 
                            accept="image/*" 
                            multiple 
                            onChange={handlePhotoUpload}
                            className="hidden"
                            id="photo-upload"
                          />
                          <label htmlFor="photo-upload" className="flex flex-col items-center cursor-pointer">
                            <Camera className="h-8 w-8 text-muted-foreground mb-2" />
                            <span className="text-sm text-muted-foreground">Click to upload photos</span>
                          </label>
                        </div>
                        {incidentPhotos.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {incidentPhotos.map((photo, idx) => (
                              <div key={idx} className="relative">
                                <img src={photo} alt={`Photo ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                                <Button 
                                  variant="destructive" 
                                  size="icon" 
                                  className="absolute -top-2 -right-2 h-5 w-5"
                                  onClick={() => setIncidentPhotos(incidentPhotos.filter((_, i) => i !== idx))}
                                >
                                  <XCircle className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                  <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => setShowIncidentDialog(false)} className="hover:bg-muted/50">Cancel</Button>
                    <Button 
                      onClick={handleSaveIncident}
                      className="ml-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md text-white border-0"
                    >
                      {selectedIncident ? "Update" : "Report"} Incident
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Lab</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredIncidents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No incidents found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredIncidents.map(incident => (
                        <TableRow key={incident.id}>
                          <TableCell className="font-medium">{format(parseISO(incident.date), "MMM d, yyyy")}</TableCell>
                          <TableCell>{getLabName(incident.labId)}</TableCell>
                          <TableCell>
                            <div>{incident.studentName}</div>
                            <div className="text-xs text-muted-foreground">{incident.studentId}</div>
                          </TableCell>
                          <TableCell>{incident.itemBroken}</TableCell>
                          <TableCell>Ksh {incident.costEstimate}</TableCell>
                          <TableCell>
                            <Badge variant={
                              incident.status === "pending" ? "destructive" : "default"
                            }>
                              {incident.status === "pending" ? "Pending" : "Cleared"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => { 
                                setSelectedIncident(incident); 
                                setIncidentForm(incident); 
                                setIncidentPhotos(incident.photos);
                                setShowIncidentDialog(true); 
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clearance Tab */}
          <TabsContent value="clearance" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-semibold text-foreground">Student Clearance</h2>
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search students..." 
                    className="pl-9 w-64"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by Class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {uniqueClasses.map((cls) => (
                      <SelectItem key={cls} value={cls}>
                        {cls}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-200/50 dark:border-green-800/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-2xl font-bold text-foreground">{graduatingStudents.filter(s => s.effectiveStatus === "cleared").length}</p>
                        <p className="text-sm text-muted-foreground">Cleared</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-200/50 dark:border-amber-800/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Clock className="h-8 w-8 text-amber-600" />
                      <div>
                        <p className="text-2xl font-bold text-foreground">{graduatingStudents.filter(s => s.effectiveStatus === "pending").length}</p>
                        <p className="text-sm text-muted-foreground">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-200/50 dark:border-red-800/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <XCircle className="h-8 w-8 text-red-600" />
                      <div>
                        <p className="text-2xl font-bold text-foreground">{graduatingStudents.filter(s => s.effectiveStatus === "not-cleared").length}</p>
                        <p className="text-sm text-muted-foreground">Not Cleared</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Graduating Students</CardTitle>
                <CardDescription>Review and manage clearance status for graduating students</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Incidents</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {graduatingStudents
                      .filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.id.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map(student => {
                        const studentIncidents = getStudentIncidents(student.id);
                        const pendingIncidents = studentIncidents.filter(i => i.status === "pending");
                        return (
                          <TableRow key={student.id}>
                            <TableCell className="font-mono">{student.id}</TableCell>
                            <TableCell className="font-medium">{student.name}</TableCell>
                            <TableCell>{student.class}</TableCell>
                            <TableCell>
                              {studentIncidents.length > 0 ? (
                                <Badge variant={pendingIncidents.length > 0 ? "destructive" : "secondary"}>
                                  {studentIncidents.length} incident{studentIncidents.length !== 1 ? "s" : ""}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">None</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                student.effectiveStatus === "cleared" ? "default" :
                                student.effectiveStatus === "not-cleared" ? "destructive" : "secondary"
                              }>
                                {student.effectiveStatus === "cleared" ? "Cleared" :
                                 student.effectiveStatus === "not-cleared" ? "Not Cleared" : "Pending"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Dialog open={showClearanceDialog && selectedStudent?.id === student.id} onOpenChange={(open) => {
                                  setShowClearanceDialog(open);
                                  if (!open) setSelectedStudent(null);
                                }}>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="icon" onClick={() => setSelectedStudent(student)}>
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-lg">
                                    <DialogHeader>
                                      <DialogTitle>Student Clearance - {student.name}</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <Label className="text-muted-foreground">Student ID</Label>
                                          <p className="font-mono">{student.id}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Class</Label>
                                          <p>{student.class}</p>
                                        </div>
                                      </div>

                                      <div>
                                        <Label className="text-muted-foreground mb-2 block">Incident History</Label>
                                        {studentIncidents.length === 0 ? (
                                          <p className="text-sm text-muted-foreground">No incidents recorded</p>
                                        ) : (
                                          <div className="space-y-2">
                                            {studentIncidents.map(inc => (
                                              <div key={inc.id} className="p-3 bg-muted/50 rounded-lg">
                                                <div className="flex justify-between items-start">
                                                  <div>
                                                    <p className="font-medium">{inc.itemBroken}</p>
                                                    <p className="text-sm text-muted-foreground">{getLabName(inc.labId)} - {format(parseISO(inc.date), "MMM d, yyyy")}</p>
                                                  </div>
                                                  <Badge variant={
                                                    inc.status === "pending" ? "destructive" : "default"
                                                  }>
                                                    {inc.status === "pending" ? "Pending" : "Cleared"}
                                                  </Badge>
                                                </div>
                                                  <p className="text-sm mt-1 text-destructive">Fine: Ksh {inc.costEstimate}</p>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {student.effectiveStatus === "not-cleared" && (
                                        <div>
                                          <Label className="text-muted-foreground">Reason Not Cleared</Label>
                                          <p className="text-sm text-destructive font-semibold">{student.effectiveReason || student.clearanceReason}</p>
                                          {student.hasPendingIncidents && (
                                              <p className="text-xs text-muted-foreground mt-1">
                                                * This student has pending lab incidents that must be resolved first.
                                              </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <DialogFooter className="flex-col sm:flex-row gap-2">
                                      <Button 
                                        variant="outline" 
                                        className="w-full sm:w-auto"
                                        onClick={() => {
                                          const reason = prompt("Enter reason for not clearing:");
                                          if (reason) {
                                            handleClearStudent(student, false, reason);
                                          }
                                        }}
                                      >
                                        <XCircle className="h-4 w-4 mr-2" />
                                        Mark Not Cleared
                                      </Button>
                                      <Button 
                                        className="w-full sm:w-auto"
                                        onClick={() => handleClearStudent(student, true)}
                                        disabled={student.hasPendingIncidents}
                                        title={student.hasPendingIncidents ? "Resolve incidents first" : "Clear Student"}
                                      >
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        {student.hasPendingIncidents ? "Blocked by Incidents" : "Clear Student"}
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => {
                                    toast.success("Generating clearance report...");
                                    window.print();
                                  }}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
            </div>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default LabManagement;